import { AppBarContainer } from 'reusable/mui/app-bar-container'
import { observer } from 'mobx-react-lite'
import { useLocation, useNavigate } from 'react-router-dom'
import RouteRenderer from './route-renderer/RouteRenderer'
import { MainContainerStore, MainContainerStoreProvider } from './MainContainerStore'
import useStore from 'reusable/mobx-store-utils'
import { useOrgGuardStore } from '../OrgGuardStore'
import RouterStore, { RouterStoreProvider } from '../RouterStore'
import NavigationMenu from './nav-drawer/NavigationMenu'
import variables from 'variables/variables'
import NotificationsToggler from './notifications/NotificationsToggler'
import Notifications from './notifications/Notifications'
import { NotificationsStore, NotificationsStoreProvider } from './notifications/NotificationsStore'
import { useEffect } from 'react'
import { useWebSocketStore } from 'websockets/WebSocketStore'
import useWindowSize from 'reusable/hooks/useWindowSize'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import HelperMenu from './HelperMenu'
import ProfileMenu from './profile-menu/ProfileMenu'
import { LoadingModal } from 'reusable/mui/modals/LoadingModal'
import ExtendedMenu from './nav-drawer/ExtendedMenu'
import Drawer from '@mui/material/Drawer'
import Menu from '@mui/icons-material/Menu'
import { useAuthStore } from 'auth/AuthStore'
import { useTheme } from 'theme/useTheme'
import { OrgSubscriptionTierStore, OrgSubscriptionTierStoreProvider } from './org-subscription-tiers/OrgSubscriptionTierStore'
import AnnouncementStore, { AnnouncementStoreProvider } from './announcement/AnnouncementStore'
import { notNull } from '@gap/utils/utils'
import PrintStore, { PrintStoreProvider } from 'reusable/printing/PrintStore'
import OrgUserUpgrade from './org-subscription-tiers/OrgUpgrade'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import userCache from 'cache/userCache'
import { useThemeStore } from 'theme/ThemeStore'
import GooseBot from 'reusable/goose/GooseBot'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import XpmConnectionStore, { XpmConnectionStoreProvider } from 'features/gap-components/xero-xpm-connect/XpmConnectionStore'
import { useCacheStore } from 'cache/CacheStore'
import useInitialMount from 'reusable/hooks/useInitialMount'

export default observer(function MainContainer() {
   const theme = useTheme()
   const themeStore = useThemeStore()
   const orgGuardStore = useOrgGuardStore()
   const authStore = useAuthStore()
   const location = useLocation()
   const navigate = useNavigate()
   const webSocketStore = useWebSocketStore()
   const windowSize = useWindowSize()
   const regionRouterStore = useRegionRouterStore()
   const routerStore = useStore(sp => new RouterStore(sp), { navigate, orgGuardStore, location, regionRouterStore })
   const orgSubscriptionTierStore = useStore((sp) => new OrgSubscriptionTierStore(sp), { orgGuardStore })
   const mainContainerStore = useStore(sp => new MainContainerStore(sp), { orgGuardStore, webSocketStore, routerStore, windowSize, theme, orgSubscriptionTierStore })
   const notificationStore = useStore((sp) => new NotificationsStore(sp), { mainContainerStore, webSocketStore, routerStore })
   const announcementStore = useStore(sp => new AnnouncementStore(sp), { webSocketStore, orgGuardStore, userInfo: orgGuardStore.userData })
   const printStore = useStore((sp) => new PrintStore(sp), { authStore })
   const cacheStore = useCacheStore()
   const xpmConnectionStore = useStore(sp => new XpmConnectionStore(sp), { orgGuardStore, announcementStore, cacheStore })

   // Redeem an XPM OAuth callback here rather than in the integration card: the card only
   // exists while its modal is open, so a return that lands anywhere else would drop the code.
   useInitialMount(() => {
      xpmConnectionStore.redeemCallbackIfPresent()
   })

   useEffect(() => {
      if (orgGuardStore.userData.userOrg.orgType === 'advisor' && !userCache.serviceUser.get()) {
         orgSubscriptionTierStore.fetch()
      }
   }, [orgGuardStore.userData, orgSubscriptionTierStore])

   useEffect(() => {
      (async () => {
         // fetch announcements
         await announcementStore.fetchAnnouncements()

         // subscribe to announcements
         await announcementStore.subscribeToAnnouncements({ orgId: notNull(orgGuardStore.orgId), userId: orgGuardStore.userData.id })
      })()
   }, [announcementStore, orgGuardStore.orgId, orgGuardStore.userData])

   useEffect(() => {
      if (!orgGuardStore.loading) {
         notificationStore.subscribeToNotificationEvents()
      }
   },
      [notificationStore, orgGuardStore.loading]
   )

   useEffect(() => {
      if (!orgGuardStore.loading) {
         mainContainerStore.subscribeToMigrationJobCompletion()
      }
   }, [mainContainerStore, orgGuardStore.loading])

   const contentPadding = windowSize.screenLayout !== 'mobile' ? { py: `${variables.contentPaddingY}px`, px: `${variables.contentPaddingX}px`, mb: 6 } : {}
   const appBarSx = windowSize.screenLayout !== 'mobile' ? { backgroundColor: themeStore.panelColors.panelBackground, color: themeStore.panelColors.text } : {}

   return (
      <RouterStoreProvider store={routerStore}>
         <PrintStoreProvider store={printStore}>
            <MainContainerStoreProvider store={mainContainerStore}>
               <NotificationsStoreProvider store={notificationStore}>
                  <OrgSubscriptionTierStoreProvider store={orgSubscriptionTierStore}>
                     <AnnouncementStoreProvider store={announcementStore}>
                      <XpmConnectionStoreProvider store={xpmConnectionStore}>
                        <Box sx={{
                           display: 'flex',
                           flexGrow: 1,
                           height: '100vh',
                           overflow: 'hidden',
                           backgroundColor: themeStore.backgroundColor,
                           transition: (!mainContainerStore.fullscreenSlideOpen && !mainContainerStore.skipTransition) ? 'opacity 300ms linear' : 'none',
                           opacity: mainContainerStore.fullscreenSlideOpen ? 0 : 1,
                           ...(themeStore.backgroundImage ? {
                              backgroundImage: themeStore.backgroundImage,
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                           } : {})
                        }}>
                           {/* Bounds the menu to the viewport so its own scroller engages. Left
                               unbounded, `ExtendedMenu`'s `height: 100%` collapses to auto, a menu
                               taller than the screen stretches this row, and the document scrolls
                               alongside the app's one content scrollbar. */}
                           <Box sx={{ height: '100vh' }}>
                              <NavigationMenu />
                           </Box>

                           <Stack sx={{ flexGrow: 1, height: '100%' }}>
                              <AppBarContainer
                                 centredBanners={mainContainerStore.centredBanners}
                                 rightButtons={<AppBarActionButtons />}
                                 appBarSx={{ ...appBarSx, borderBottom: `1px solid ${themeStore.panelColors.panelBorder}`, backgroundColor: themeStore.backgroundImageUrl ? 'transparent' : themeStore.backgroundColor }}
                                 appBarProps={{ elevation: 0 }}
                                 {...windowSize.screenLayout === 'mobile' ? { leftButtonIcon: <Menu onClick={mainContainerStore.menuState.open} sx={{ ml: 1 }} /> } : { noContentPadding: true }}>
                                 {!orgGuardStore.loading && <Box sx={{ mb: 1.5, ...contentPadding }}>
                                    <RouteRenderer />
                                 </Box>}
                              </AppBarContainer>
                           </Stack>
                        </Box>

                        <LoadingModal
                           title={orgGuardStore.loader.loaderText || "Loading..."}
                           visible={orgGuardStore.loading}
                        />
                        {windowSize.screenLayout !== 'desktop' && routerStore.menu.length && <Drawer
                           open={mainContainerStore.menuState.visible}
                           onClose={mainContainerStore.menuState.close}
                           onClick={mainContainerStore.menuState.close}>
                           <ExtendedMenu />
                        </Drawer>}

                        <Notifications />
                        <GapDialog
                           title={mainContainerStore.menuComponentModal.title}
                           modalState={mainContainerStore.menuComponentModal.state}
                           size='medium'
                        >
                           {mainContainerStore.menuComponentModal.component && <mainContainerStore.menuComponentModal.component />}
                        </GapDialog>
                        <GooseBot />
                        <OrgUserUpgrade state={orgSubscriptionTierStore.upgradePlanModal} />
                      </XpmConnectionStoreProvider>
                     </AnnouncementStoreProvider>
                  </OrgSubscriptionTierStoreProvider>
               </NotificationsStoreProvider>
            </MainContainerStoreProvider>
         </PrintStoreProvider>
      </RouterStoreProvider>
   )
})

const AppBarActionButtons = observer(function AppBarActionButtons() {
   const authStore = useAuthStore()
   const orgGuardStore = useOrgGuardStore()

   return <Stack direction="row" gap={0.5}>
      {!orgGuardStore.userIsDeactivated && orgGuardStore.org?.orgType === 'advisor' && <HelperMenu feedbackType='feedback-from-app-bar' />}
      {!orgGuardStore.userIsDeactivated && <NotificationsToggler />}
      {!authStore.isGuest && <ProfileMenu />}
   </Stack>
})
